import React, { Fragment, useState } from 'react'

import PropTypes from 'prop-types'
import { Pencil, Trash, Eye } from '@entrata/portals-common/lib/icons'
import DeleteConfirmation from 'portal-modules/common/components/DeleteConfirmation'

import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { ModalControl } from '@entrata/portals-common/lib/components'
import { InformationModal } from 'portal-modules/common/components/modals'
import { PAYMENTS } from 'portal-modules/payments/constants/urls'
import { withRouter } from 'react-router-dom'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'

const StyledCard = styled('div')`
  background-color: ${({
    theme,
    deleteAccess,
    editAccess,
    isRefundAccount,
    isAutoPayment,
  }) =>
    !isRefundAccount && !isAutoPayment
      ? !deleteAccess && !editAccess
        ? theme.mode.card_disabled_background
        : theme.mode.card_background_color
      : theme.mode.card_background_color};
  padding: 16px;
  border: 1px solid ${({ theme }) => getEmbeddedViewFlag() ? theme.embedded.input_border_outline : theme.mode.card_border_color};
  box-shadow: ${({ theme }) => theme.mode.card_shadow};
  border-radius: ${({ theme }) => theme.mode.card_border_radius};
`

const StyledHeader = styled('header')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > :first-child {
    word-break: break-word;
  }
`

const StyledButton = styled('button')`
  background: transparent;
  border: none;
  color: ${({ theme }) => getEmbeddedViewFlag() ? theme.button_primary.background : theme.blue.system_blue};
  padding: 0;
  margin-left: 8px;
  vertical-align: middle;
`

/** A component for creating cards with edit and delete actions.
 * Allows for customization of the title and content of the card. */

const ActionCard = ({
  id,
  children,
  confirmationText,
  onDelete,
  onEdit,
  onView,
  title,
  connectedAutoPayments,
  isSepa,
  hasRefundAccount,
  showView,
  allowedEdit,
  history,
  isAddAutoPaymentAllowed,
  isDeleteAutoPaymentAllowed,
  isEditAutoPaymentAllowed,
  deletePaymentMethodAccess,
  editPaymentMethodAccess,
  addPaymentMethodAccess,
  isRefundAccount,
  isAutoPayment,
  isFromWallet,
  ...rest
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false)

  const cancelDelete = () => {
    setConfirmDelete(false)
  }

  const handleDelete = () => {
    setConfirmDelete(true)
  }

  const handleEdit = () => {
    onEdit(id)
  }

  const handleView = () => {
    onView(id)
  }

  const handleAutoPaymentEdit = () => history.push(PAYMENTS.AUTO_PAYMENTS)

  const handleRefundAccountEdit = () => history.push(PAYMENTS.ADD_REFUND_METHOD)

  const handlePaymentDelete = () => onDelete()

  const hasDelete = typeof onDelete === 'function'
  const hasAutoPayments =
    connectedAutoPayments && connectedAutoPayments.length > 0

  let allowedEditCondition = allowedEdit
  if (allowedEdit && (isFromWallet || isAutoPayment)) {
    allowedEditCondition =
      ((!isAutoPayment && editPaymentMethodAccess) ||
        (isAutoPayment && allowedEdit)) &&
      !isSepa
  }

  return (
    <StyledCard
      deleteAccess={deletePaymentMethodAccess}
      editAccess={editPaymentMethodAccess}
      isRefundAccount={isRefundAccount}
      isAutoPayment={isAutoPayment}
      {...rest}
    >
      {hasDelete && confirmDelete && !hasAutoPayments ? (
        <DeleteConfirmation onCancel={cancelDelete} onDelete={onDelete}>
          {confirmationText}
        </DeleteConfirmation>
      ) : (
        <Fragment>
          <StyledHeader>
            {title}
            <div
              css={css`
                display: flex;
              `}
            >
              {showView && typeof onView === 'function' && (
                <StyledButton onClick={handleView}>
                  <Eye size={20} />
                </StyledButton>
              )}

              {allowedEditCondition && typeof onEdit === 'function' && (
                <StyledButton onClick={handleEdit}>
                  <Pencil size={20} />
                </StyledButton>
              )}

              {hasDelete &&
                (!hasAutoPayments && !hasRefundAccount ? (
                  !isRefundAccount && !isAutoPayment ? (
                    deletePaymentMethodAccess ? (
                      <StyledButton onClick={handleDelete}>
                        <Trash size={20} />
                      </StyledButton>
                    ) : (
                      <></>
                    )
                  ) : (
                    <StyledButton onClick={handleDelete}>
                      <Trash size={20} />
                    </StyledButton>
                  )
                ) : (
                  <ModalControl>
                    {({ onClick, ref, ...modalProps }) => (
                      <Fragment>
                        {!isRefundAccount && !isAutoPayment ? (
                          deletePaymentMethodAccess ? (
                            <span onClick={onClick} ref={ref}>
                              <StyledButton>
                                <Trash size={20} />
                              </StyledButton>
                            </span>
                          ) : (
                            <></>
                          )
                        ) : (
                          <span onClick={onClick} ref={ref}>
                            <StyledButton>
                              <Trash size={20} />
                            </StyledButton>
                          </span>
                        )}
                        <InformationModal
                          {...modalProps}
                          isSepa={isSepa}
                          title={__('For Your Information')}
                          handlePaymentDelete={handlePaymentDelete}
                          handleAutoPaymentEdit={handleAutoPaymentEdit}
                          connectedAutoPayments={connectedAutoPayments}
                          hasRefundAccount={hasRefundAccount}
                          handleRefundAccountEdit={handleRefundAccountEdit}
                          addPaymentMethodAccess={addPaymentMethodAccess}
                          isAddAutoPaymentAllowed={isAddAutoPaymentAllowed}
                          isEditAutoPaymentAllowed={isEditAutoPaymentAllowed}
                          isDeleteAutoPaymentAllowed={
                            isDeleteAutoPaymentAllowed
                          }
                          {...rest}
                        />
                      </Fragment>
                    )}
                  </ModalControl>
                ))}
            </div>
          </StyledHeader>
          {children}
        </Fragment>
      )}
    </StyledCard>
  )
}

ActionCard.propTypes = {
  /** Content to be rendered in the body of the `Card`. */
  children: PropTypes.any,
  /** Text to display when confirming delete. */
  confirmationText: PropTypes.string,
  /** Function to call when user confirms a delete action. */
  onDelete: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /** Function to call when user clicks the edit button. */
  onEdit: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /** Function to call when user clicks the view button */
  onView: PropTypes.func,
  /** Content to render in the header of the card. */
  title: PropTypes.any.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** flag to check if its for payment method */
  isFromWallet: PropTypes.bool,
  connectedAutoPayments: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number,
  ]),
  isSepa: PropTypes.bool,
  history: PropTypes.object,
  hasRefundAccount: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  showView: PropTypes.bool,
  allowedEdit: PropTypes.bool,
  isAddAutoPaymentAllowed: PropTypes.bool,
  isDeleteAutoPaymentAllowed: PropTypes.bool,
  isEditAutoPaymentAllowed: PropTypes.bool,
  deletePaymentMethodAccess: PropTypes.bool,
  editPaymentMethodAccess: PropTypes.bool,
  addPaymentMethodAccess: PropTypes.bool,
  isRefundAccount: PropTypes.bool,
  isAutoPayment: PropTypes.bool,
}

ActionCard.defaultProps = {
  confirmationText: __('Do you want to delete this?'),
  allowedEdit: true,
  deletePaymentMethodAccess: true,
  editPaymentMethodAccess: true,
  addPaymentMethodAccess: true,
}

export default withRouter(ActionCard)